import React from "react";

import {
    Box,
    Paper,
    Typography,
    Divider,
    Button,
} from "@mui/material";

import {
    SupportAgent,
    Email,
    WhatsApp,
    Instagram,
    YouTube,
    Telegram,
    Facebook,
    ChatBubbleOutline,
    ArrowForward,
    CheckCircle,
    HelpOutline,
    AccountCircle,
    Payments,
    Security,
    Groups,
    OpenInNew,
} from "@mui/icons-material";

const Support = () => {

    // ----------------------------------------------------
    // SUPPORT CARD
    // ----------------------------------------------------

    const SupportCard = ({
        icon,
        title,
        description,
        actionText,
        href,
        primary = false,
    }: {
        icon: React.ReactNode;
        title: string;
        description: string;
        actionText: string;
        href: string;
        primary?: boolean;
    }) => {
        return (
            <Paper
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                elevation={0}
                sx={{
                    textDecoration: "none",

                    position: "relative",

                    overflow: "hidden",

                    p: {
                        xs: 2.5,
                        sm: 3,
                    },

                    borderRadius: "20px",

                    background: primary
                        ? "linear-gradient(145deg, rgba(23,55,42,.85), rgba(12,27,21,.96))"
                        : "linear-gradient(145deg, rgba(255,255,255,.045), rgba(255,255,255,.015))",

                    border: primary
                        ? "1px solid rgba(67,244,166,.18)"
                        : "1px solid rgba(255,255,255,.075)",

                    transition: "all .25s ease",

                    "&:hover": {
                        transform: "translateY(-4px)",

                        borderColor: primary
                            ? "rgba(67,244,166,.38)"
                            : "rgba(255,255,255,.15)",

                        boxShadow: primary
                            ? "0 18px 45px rgba(67,244,166,.09)"
                            : "0 18px 45px rgba(0,0,0,.25)",
                    },

                    "&::before": {
                        content: '""',

                        position: "absolute",

                        width: "130px",
                        height: "130px",

                        right: "-70px",
                        top: "-70px",

                        borderRadius: "50%",

                        background: primary
                            ? "rgba(67,244,166,.06)"
                            : "rgba(255,255,255,.025)",

                        filter: "blur(10px)",
                    },
                }}
            >

                {/* TOP */}

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                    }}
                >

                    <Box
                        sx={{
                            width: 50,
                            height: 50,

                            borderRadius: "15px",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            color: primary
                                ? "#43f4a6"
                                : "#b9c3cc",

                            background: primary
                                ? "rgba(67,244,166,.09)"
                                : "rgba(255,255,255,.055)",

                            border: primary
                                ? "1px solid rgba(67,244,166,.17)"
                                : "1px solid rgba(255,255,255,.08)",
                        }}
                    >
                        {icon}
                    </Box>

                    <OpenInNew
                        sx={{
                            fontSize: 17,
                            color: "#58636e",
                        }}
                    />

                </Box>

                {/* CONTENT */}

                <Typography
                    sx={{
                        mt: 2.2,

                        fontSize: {
                            xs: "16px",
                            sm: "17px",
                        },

                        fontWeight: 700,

                        color: "#edf2f5",
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    sx={{
                        mt: .7,

                        fontSize: "11px",

                        lineHeight: 1.7,

                        color: "#75808b",

                        minHeight: "38px",
                    }}
                >
                    {description}
                </Typography>

                {/* ACTION */}

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: .7,

                        mt: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "11px",

                            fontWeight: 700,

                            color: primary
                                ? "#43f4a6"
                                : "#aeb7c0",
                        }}
                    >
                        {actionText}
                    </Typography>

                    <ArrowForward
                        sx={{
                            fontSize: 15,

                            color: primary
                                ? "#43f4a6"
                                : "#78828c",
                        }}
                    />
                </Box>

            </Paper>
        );
    };


    // ----------------------------------------------------
    // QUICK HELP CARD
    // ----------------------------------------------------

    const QuickHelp = ({
        icon,
        title,
        description,
    }: {
        icon: React.ReactNode;
        title: string;
        description: string;
    }) => {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",

                    gap: 1.5,

                    p: 1.5,

                    borderRadius: "15px",

                    background:
                        "rgba(255,255,255,.025)",

                    border:
                        "1px solid rgba(255,255,255,.055)",

                    transition: "all .2s ease",

                    "&:hover": {
                        background:
                            "rgba(255,255,255,.045)",

                        borderColor:
                            "rgba(67,244,166,.13)",
                    },
                }}
            >

                <Box
                    sx={{
                        width: 40,
                        height: 40,

                        minWidth: 40,

                        borderRadius: "12px",

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        color: "#43f4a6",

                        background:
                            "rgba(67,244,166,.065)",

                        border:
                            "1px solid rgba(67,244,166,.1)",
                    }}
                >
                    {icon}
                </Box>

                <Box>

                    <Typography
                        sx={{
                            fontSize: "12px",

                            fontWeight: 700,

                            color: "#dfe5e9",
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "9px",

                            color: "#68737d",

                            mt: ".25rem",

                            lineHeight: 1.5,
                        }}
                    >
                        {description}
                    </Typography>

                </Box>

            </Box>
        );
    };


    // ----------------------------------------------------
    // SOCIAL BUTTON
    // ----------------------------------------------------

    const SocialButton = ({
        icon,
        href,
        label,
        color,
    }: {
        icon: React.ReactNode;
        href: string;
        label: string;
        color: string;
    }) => {
        return (
            <Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    textDecoration: "none",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    gap: 1,

                    px: 1.5,

                    height: 42,

                    borderRadius: "13px",

                    color,

                    background:
                        "rgba(255,255,255,.03)",

                    border:
                        "1px solid rgba(255,255,255,.07)",

                    transition: "all .2s ease",

                    "&:hover": {
                        background:
                            "rgba(255,255,255,.065)",

                        borderColor:
                            `${color}55`,

                        transform:
                            "translateY(-2px)",
                    },
                }}
            >

                {icon}

                <Typography
                    sx={{
                        fontSize: "10px",

                        fontWeight: 600,

                        color: "#aeb7c0",
                    }}
                >
                    {label}
                </Typography>

            </Box>
        );
    };


    // ----------------------------------------------------
    // PAGE
    // ----------------------------------------------------

    return (
        <Box
            sx={{
                minHeight: "100vh",

                position: "relative",

                overflow: "hidden",

                background:
                    "radial-gradient(circle at 10% 5%, rgba(43,255,157,.065), transparent 25%), radial-gradient(circle at 90% 15%, rgba(43,255,157,.035), transparent 25%), #080c10",

                color: "#fff",

                px: {
                    xs: 1.5,
                    sm: 3,
                    md: 4,
                },

                py: {
                    xs: 3,
                    sm: 5,
                    md: 6,
                },
            }}
        >

            {/* ------------------------------------------------ */}
            {/* BACKGROUND GRID */}
            {/* ------------------------------------------------ */}

            <Box
                sx={{
                    position: "absolute",

                    inset: 0,

                    pointerEvents: "none",

                    opacity: .12,

                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)",

                    backgroundSize: "45px 45px",
                }}
            />


            {/* ------------------------------------------------ */}
            {/* MAIN CONTAINER */}
            {/* ------------------------------------------------ */}

            <Box
                sx={{
                    position: "relative",

                    zIndex: 1,

                    maxWidth: "1050px",

                    width: "100%",

                    mx: "auto",
                }}
            >


                {/* ------------------------------------------------ */}
                {/* HERO */}
                {/* ------------------------------------------------ */}

                <Box
                    sx={{
                        textAlign: "center",

                        mb: {
                            xs: 3,
                            sm: 4,
                        },
                    }}
                >

                    {/* STATUS */}

                    <Box
                        sx={{
                            display: "inline-flex",

                            alignItems: "center",

                            gap: .8,

                            px: 1.5,

                            py: .65,

                            mb: 2,

                            borderRadius: "30px",

                            background:
                                "rgba(67,244,166,.055)",

                            border:
                                "1px solid rgba(67,244,166,.12)",
                        }}
                    >

                        <Box
                            sx={{
                                width: 7,
                                height: 7,

                                borderRadius: "50%",

                                background:
                                    "#43f4a6",

                                boxShadow:
                                    "0 0 10px rgba(67,244,166,.7)",
                            }}
                        />

                        <Typography
                            sx={{
                                color: "#43f4a6",

                                fontSize: "9px",

                                fontWeight: 700,

                                letterSpacing: "1px",

                                textTransform:
                                    "uppercase",
                            }}
                        >
                            Support Available
                        </Typography>

                    </Box>


                    {/* MAIN ICON */}

                    <Box
                        sx={{
                            width: 72,
                            height: 72,

                            mx: "auto",

                            mb: 2,

                            borderRadius: "21px",

                            display: "flex",

                            alignItems: "center",

                            justifyContent: "center",

                            color: "#43f4a6",

                            background:
                                "linear-gradient(145deg, rgba(67,244,166,.12), rgba(67,244,166,.035))",

                            border:
                                "1px solid rgba(67,244,166,.17)",

                            boxShadow:
                                "0 0 45px rgba(67,244,166,.055)",
                        }}
                    >

                        <SupportAgent
                            sx={{
                                fontSize: 37,
                            }}
                        />

                    </Box>


                    {/* TITLE */}

                    <Typography
                        sx={{
                            fontSize: {
                                xs: "27px",
                                sm: "34px",
                                md: "38px",
                            },

                            fontWeight: 800,

                            letterSpacing:
                                "-1px",

                            color: "#f1f5f7",
                        }}
                    >
                        How can we help?
                    </Typography>


                    {/* DESCRIPTION */}

                    <Typography
                        sx={{
                            maxWidth: "560px",

                            mx: "auto",

                            mt: 1,

                            color: "#737e89",

                            fontSize: {
                                xs: "11px",
                                sm: "12px",
                            },

                            lineHeight: 1.8,
                        }}
                    >
                        Our support team is here to help you with
                        your account, payments, membership and
                        other Growniverse related questions.
                    </Typography>

                </Box>


                {/* ------------------------------------------------ */}
                {/* MAIN SUPPORT PANEL */}
                {/* ------------------------------------------------ */}

                <Paper
                    elevation={0}
                    sx={{
                        p: {
                            xs: 2,
                            sm: 3,
                            md: 4,
                        },

                        borderRadius: "26px",

                        background:
                            "linear-gradient(145deg, rgba(18,25,30,.97), rgba(9,14,18,.98))",

                        border:
                            "1px solid rgba(255,255,255,.075)",

                        boxShadow:
                            "0 30px 80px rgba(0,0,0,.3)",
                    }}
                >


                    {/* PANEL TITLE */}

                    <Box
                        sx={{
                            display: "flex",

                            alignItems: "center",

                            gap: 1.5,

                            mb: 2.5,
                        }}
                    >

                        <Box
                            sx={{
                                width: 44,
                                height: 44,

                                borderRadius: "14px",

                                display: "flex",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "center",

                                color: "#43f4a6",

                                background:
                                    "rgba(67,244,166,.07)",

                                border:
                                    "1px solid rgba(67,244,166,.12)",
                            }}
                        >
                            <ChatBubbleOutline />
                        </Box>

                        <Box>

                            <Typography
                                sx={{
                                    fontSize: "17px",

                                    fontWeight: 700,

                                    color: "#edf2f5",
                                }}
                            >
                                Contact Support
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "10px",

                                    color: "#697481",

                                    mt: ".2rem",
                                }}
                            >
                                Select the channel that works best
                                for you.
                            </Typography>

                        </Box>

                    </Box>


                    <Divider
                        sx={{
                            mb: 2.5,

                            borderColor:
                                "rgba(255,255,255,.06)",
                        }}
                    />


                    {/* SUPPORT OPTIONS */}

                    <Box
                        sx={{
                            display: "grid",

                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr",
                            },

                            gap: 1.5,
                        }}
                    >

                        <SupportCard
                            icon={<Email />}
                            title="Email Support"
                            description="For detailed questions, account issues or general assistance."
                            actionText="admin@growniverse.com"
                            href="mailto:admin@growniverse.com"
                            primary
                        />

                        <SupportCard
                            icon={<WhatsApp />}
                            title="WhatsApp Support"
                            description="For quick questions and direct communication with our team."
                            actionText="Start WhatsApp Chat"
                            href="https://wa.me/918091322146"
                            primary
                        />

                    </Box>


                    {/* ------------------------------------------------ */}
                    {/* QUICK HELP */}
                    {/* ------------------------------------------------ */}

                    <Box
                        sx={{
                            mt: 3,
                        }}
                    >

                        <Typography
                            sx={{
                                fontSize: "10px",

                                fontWeight: 700,

                                letterSpacing:
                                    "1.2px",

                                color: "#697481",

                                textTransform:
                                    "uppercase",

                                mb: 1.5,
                            }}
                        >
                            What can we help with?
                        </Typography>


                        <Box
                            sx={{
                                display: "grid",

                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                    md: "repeat(4, 1fr)",
                                },

                                gap: 1,
                            }}
                        >

                            <QuickHelp
                                icon={
                                    <AccountCircle
                                        sx={{
                                            fontSize: 19,
                                        }}
                                    />
                                }
                                title="Account"
                                description="Profile & account help"
                            />

                            <QuickHelp
                                icon={
                                    <Payments
                                        sx={{
                                            fontSize: 19,
                                        }}
                                    />
                                }
                                title="Payments"
                                description="Payment related queries"
                            />

                            <QuickHelp
                                icon={
                                    <Security
                                        sx={{
                                            fontSize: 19,
                                        }}
                                    />
                                }
                                title="Security"
                                description="Password & security"
                            />

                            <QuickHelp
                                icon={
                                    <Groups
                                        sx={{
                                            fontSize: 19,
                                        }}
                                    />
                                }
                                title="Membership"
                                description="Package & membership"
                            />

                        </Box>

                    </Box>

                </Paper>


                {/* ------------------------------------------------ */}
                {/* SOCIAL MEDIA */}
                {/* ------------------------------------------------ */}

                <Paper
                    elevation={0}
                    sx={{
                        mt: 2,

                        p: {
                            xs: 2,
                            sm: 2.5,
                        },

                        borderRadius: "21px",

                        background:
                            "rgba(255,255,255,.025)",

                        border:
                            "1px solid rgba(255,255,255,.065)",
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",

                            alignItems: {
                                xs: "flex-start",
                                sm: "center",
                            },

                            justifyContent:
                                "space-between",

                            flexDirection: {
                                xs: "column",
                                sm: "row",
                            },

                            gap: 2,
                        }}
                    >

                        <Box>

                            <Typography
                                sx={{
                                    fontSize: "13px",

                                    fontWeight: 700,

                                    color: "#e0e6ea",
                                }}
                            >
                                Stay connected
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "10px",

                                    color: "#697481",

                                    mt: ".3rem",
                                }}
                            >
                                Follow our official channels
                                for updates and announcements.
                            </Typography>

                        </Box>


                        <Box
                            sx={{
                                display: "flex",

                                gap: .7,

                                flexWrap: "wrap",
                            }}
                        >

                            <SocialButton
                                icon={
                                    <Instagram
                                        sx={{
                                            fontSize: 17,
                                        }}
                                    />
                                }
                                label="Instagram"
                                color="#df77af"
                                href="https://www.instagram.com/growniverseofficial/"
                            />

                            <SocialButton
                                icon={
                                    <YouTube
                                        sx={{
                                            fontSize: 17,
                                        }}
                                    />
                                }
                                label="YouTube"
                                color="#ff6666"
                                href="https://youtube.com/@growniverse?si=HipqeODMzh2ZDsLl"
                            />

                            <SocialButton
                                icon={
                                    <Telegram
                                        sx={{
                                            fontSize: 17,
                                        }}
                                    />
                                }
                                label="Telegram"
                                color="#55b9ee"
                                href="https://t.me/growniverse"
                            />

                            <SocialButton
                                icon={
                                    <Facebook
                                        sx={{
                                            fontSize: 17,
                                        }}
                                    />
                                }
                                label="Facebook"
                                color="#619cf1"
                                href="https://www.facebook.com/profile.php?id=61570584860719"
                            />

                        </Box>

                    </Box>

                </Paper>


                {/* ------------------------------------------------ */}
                {/* RESPONSE NOTICE */}
                {/* ------------------------------------------------ */}

                <Box
                    sx={{
                        mt: 2,

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "center",

                        gap: .8,

                        color: "#59646e",
                    }}
                >

                    <CheckCircle
                        sx={{
                            fontSize: 14,

                            color: "#43f4a6",
                        }}
                    />

                    <Typography
                        sx={{
                            fontSize: "9px",
                        }}
                    >
                        Please include your Growniverse ID when
                        contacting support.
                    </Typography>

                </Box>


                {/* ------------------------------------------------ */}
                {/* FOOTER */}
                {/* ------------------------------------------------ */}

                <Box
                    sx={{
                        textAlign: "center",

                        pt: 4,
                        pb: 2,
                    }}
                >

                    <Typography
                        sx={{
                            fontSize: "9px",

                            letterSpacing:
                                "2px",

                            color: "#3f4851",

                            textTransform:
                                "uppercase",
                        }}
                    >
                        GROWNIVERSE • SUPPORT CENTER
                    </Typography>

                </Box>

            </Box>

        </Box>
    );
};

export default Support;